import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// GH-95 Option A: practice can have multiple cadence configs, each with its
// own recipients + schedule + content selection. Renders as a list of cards;
// staff add/edit/delete cadences from here. Patient feedback section is
// opt-in per cadence (owner reports show it; ops summaries usually don't).

const DEFAULT_NEW_CADENCE = {
  cadence_name: '',
  enabled: true,
  frequency: 'daily',
  day_of_week: 'monday',
  day_of_month: 1,
  time_of_day: '08:00',
  custom_start_date: '',
  custom_end_date: '',
  recipient_emails: [],
  include_appointments: true,
  include_intake_status: true,
  include_no_shows: true,
  include_cancellations: true,
  include_feedback: true,
};

function defaultLabelFor(freq) {
  return freq === 'weekly' ? 'Weekly Report'
    : freq === 'monthly' ? 'Monthly Report'
    : freq === 'custom' ? 'Custom Report'
    : 'Daily Report';
}

function normalizeFromServer(d) {
  return {
    id: d.id,
    cadence_name: d.cadence_name || defaultLabelFor(d.frequency),
    enabled: !!d.enabled,
    frequency: d.frequency || 'daily',
    day_of_week: d.day_of_week || 'monday',
    day_of_month: d.day_of_month || 1,
    time_of_day: d.time_of_day || '08:00',
    custom_start_date: d.custom_start_date || '',
    custom_end_date: d.custom_end_date || '',
    recipient_emails: typeof d.recipient_emails === 'string'
      ? JSON.parse(d.recipient_emails || '[]')
      : (d.recipient_emails || []),
    include_appointments: !!d.include_appointments,
    include_intake_status: !!d.include_intake_status,
    include_no_shows: !!d.include_no_shows,
    include_cancellations: !!d.include_cancellations,
    include_feedback: d.include_feedback === undefined ? true : !!d.include_feedback,
  };
}

export default function PatientReportsConfig({ practiceId }) {
  const [cadences, setCadences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    if (!practiceId) return;
    setLoading(true);
    try {
      const res = await api.request(`/practices/${practiceId}/patient-reports/configs`);
      setCadences((res.data || []).map(normalizeFromServer));
    } catch (err) {
      console.error(err);
      setToast({ kind: 'error', text: err.message || 'Failed to load configs' });
    }
    setLoading(false);
  }, [practiceId]);

  useEffect(() => { load(); }, [load]);

  const showToast = (kind, text) => {
    setToast({ kind, text });
    setTimeout(() => setToast(t => (t?.text === text ? null : t)), 4000);
  };

  const addCadence = () => {
    setCadences(c => [...c, { ...DEFAULT_NEW_CADENCE, _isNew: true, _localId: Date.now() }]);
  };

  return (
    <div>
      <h3 style={s.title}>Patient Report Cadences</h3>
      <p style={s.desc}>
        Configure one or more report cadences. Each cadence can have its own
        recipients, schedule, and content. Patient feedback (ratings + recent
        comments) is opt-in per cadence.
      </p>

      {toast && <div style={s[toast.kind === 'error' ? 'errorBox' : 'success']}>{toast.text}</div>}

      {loading && cadences.length === 0 && <p style={{ color: '#9ca3af' }}>Loading...</p>}

      {cadences.map((cadence) => (
        <CadenceCard
          key={cadence.id ?? cadence._localId}
          cadence={cadence}
          practiceId={practiceId}
          onSaved={() => { load(); showToast('success', 'Cadence saved'); }}
          onDeleted={() => { load(); showToast('success', 'Cadence deleted'); }}
          onRun={(result) => showToast('success', `${result.cadenceName || 'Report'} sent to ${result.sent} of ${result.total} recipients`)}
          onError={(msg) => showToast('error', msg)}
        />
      ))}

      {!loading && cadences.length === 0 && (
        <p style={{ color: '#6b7280', fontSize: 13 }}>
          No cadences yet. Click <strong>+ Add Cadence</strong> to create one.
        </p>
      )}

      <button style={s.btnPrimary} onClick={addCadence}>+ Add Cadence</button>
    </div>
  );
}

// ── CadenceCard ────────────────────────────────────────────────────────

function CadenceCard({ cadence, practiceId, onSaved, onDeleted, onRun, onError }) {
  const [form, setForm] = useState(cadence);
  const [emailInput, setEmailInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(!!cadence._isNew);

  useEffect(() => { setForm(cadence); }, [cadence]);

  const isPersisted = !!form.id;

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const addEmail = () => {
    const email = emailInput.trim();
    if (email && !form.recipient_emails.includes(email)) {
      setField('recipient_emails', [...form.recipient_emails, email]);
      setEmailInput('');
    }
  };
  const removeEmail = (email) => {
    setField('recipient_emails', form.recipient_emails.filter(e => e !== email));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isPersisted) {
        await api.request(`/practices/${practiceId}/patient-reports/configs/${form.id}`, {
          method: 'PUT', body: JSON.stringify(form),
        });
      } else {
        await api.request(`/practices/${practiceId}/patient-reports/configs`, {
          method: 'POST', body: JSON.stringify(form),
        });
      }
      onSaved?.();
    } catch (err) {
      onError?.(err.message || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!isPersisted) {
      onDeleted?.();
      return;
    }
    if (!window.confirm(`Delete the "${form.cadence_name}" cadence? Recipients will stop receiving this report.`)) return;
    try {
      await api.request(`/practices/${practiceId}/patient-reports/configs/${form.id}`, { method: 'DELETE' });
      onDeleted?.();
    } catch (err) {
      onError?.(err.message || 'Delete failed');
    }
  };

  const runNow = async () => {
    if (!isPersisted) {
      onError?.('Save the cadence first, then Run Now.');
      return;
    }
    setRunning(true);
    try {
      const body = form.frequency === 'custom'
        ? { startDate: form.custom_start_date, endDate: form.custom_end_date }
        : {};
      const r = await api.request(`/practices/${practiceId}/patient-reports/configs/${form.id}/run`, {
        method: 'POST', body: JSON.stringify(body),
      });
      onRun?.(r.data || {});
    } catch (err) {
      onError?.(err.message || 'Failed to send report');
    }
    setRunning(false);
  };

  return (
    <div style={s.card}>
      <div style={s.cardHeader} onClick={() => setExpanded(e => !e)}>
        <div>
          <span style={{ fontSize: 11, color: form.enabled ? '#16a34a' : '#9ca3af', fontWeight: 600 }}>
            {form.enabled ? '● ENABLED' : '○ DISABLED'}
          </span>
          <span style={s.cardTitle}>{form.cadence_name || defaultLabelFor(form.frequency)}</span>
          <span style={s.cardMeta}>
            {form.frequency}
            {form.frequency === 'weekly' && ` • ${form.day_of_week}`}
            {form.frequency === 'monthly' && ` • day ${form.day_of_month}`}
            {form.frequency !== 'custom' && ` • ${form.time_of_day} UTC`}
            {' • '}
            {form.recipient_emails.length} recipient{form.recipient_emails.length === 1 ? '' : 's'}
          </span>
        </div>
        <span style={{ color: '#9ca3af', fontSize: 18 }}>{expanded ? '▾' : '▸'}</span>
      </div>

      {expanded && (
        <div style={s.cardBody}>
          <div style={s.row}>
            <label style={s.checkLabel}>
              <input type="checkbox" checked={form.enabled} onChange={e => setField('enabled', e.target.checked)} />
              Enabled (cron will fire on schedule)
            </label>
          </div>

          <div style={s.field}>
            <label style={s.label}>Cadence Name</label>
            <input type="text" style={s.input} value={form.cadence_name}
              onChange={e => setField('cadence_name', e.target.value)}
              placeholder="e.g. Daily Ops Summary" />
          </div>

          <div style={s.grid}>
            <div style={s.field}>
              <label style={s.label}>Frequency</label>
              <select style={s.input} value={form.frequency}
                onChange={e => setField('frequency', e.target.value)}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom Date Range (one-shot)</option>
              </select>
            </div>
            {form.frequency === 'weekly' && (
              <div style={s.field}>
                <label style={s.label}>Day of Week</label>
                <select style={s.input} value={form.day_of_week}
                  onChange={e => setField('day_of_week', e.target.value)}>
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(d => (
                    <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                  ))}
                </select>
              </div>
            )}
            {form.frequency === 'monthly' && (
              <div style={s.field}>
                <label style={s.label}>Day of Month (1-28)</label>
                <input type="number" min="1" max="28" style={s.input} value={form.day_of_month}
                  onChange={e => setField('day_of_month', Math.max(1, Math.min(28, parseInt(e.target.value) || 1)))} />
              </div>
            )}
            {form.frequency === 'custom' && (
              <>
                <div style={s.field}>
                  <label style={s.label}>Start Date</label>
                  <input type="date" style={s.input} value={form.custom_start_date}
                    onChange={e => setField('custom_start_date', e.target.value)} />
                </div>
                <div style={s.field}>
                  <label style={s.label}>End Date</label>
                  <input type="date" style={s.input} value={form.custom_end_date}
                    onChange={e => setField('custom_end_date', e.target.value)} />
                </div>
              </>
            )}
            {form.frequency !== 'custom' && (
              <div style={s.field}>
                <label style={s.label}>Send Time (UTC)</label>
                <input type="time" style={s.input} value={form.time_of_day}
                  onChange={e => setField('time_of_day', e.target.value)} />
              </div>
            )}
          </div>

          {form.frequency === 'custom' && (
            <p style={{ fontSize: 12, color: '#6b7280', marginTop: -8, marginBottom: 16 }}>
              Custom cadences are one-shot — they don't fire from the cron. Use the Run Now button to send.
            </p>
          )}

          <div style={s.field}>
            <label style={s.label}>Include in Report</label>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {[
                { key: 'include_appointments', label: 'Appointments' },
                { key: 'include_intake_status', label: 'Intake Status' },
                { key: 'include_no_shows', label: 'No-Shows' },
                { key: 'include_cancellations', label: 'Cancellations' },
                { key: 'include_feedback', label: 'Patient Feedback' },
              ].map(opt => (
                <label key={opt.key} style={s.checkLabel}>
                  <input type="checkbox" checked={!!form[opt.key]}
                    onChange={e => setField(opt.key, e.target.checked)} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Recipients</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input type="email" style={{ ...s.input, flex: 1 }} placeholder="provider@example.com"
                value={emailInput}
                onChange={e => setEmailInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addEmail())} />
              <button style={s.btnSecondary} onClick={addEmail}>Add</button>
            </div>
            {form.recipient_emails.map(email => (
              <div key={email} style={s.emailChip}>
                {email}
                <button style={s.removeBtn} onClick={() => removeEmail(email)}>&times;</button>
              </div>
            ))}
            {form.recipient_emails.length === 0 && (
              <p style={{ color: '#9ca3af', fontSize: 12 }}>No recipients added yet.</p>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button style={s.btnPrimary} onClick={save} disabled={saving}>
              {saving ? 'Saving...' : (isPersisted ? 'Update' : 'Create Cadence')}
            </button>
            <button style={s.btnSecondary} onClick={runNow}
              disabled={running || !isPersisted || form.recipient_emails.length === 0}
              title={!isPersisted ? 'Save first' : (form.recipient_emails.length === 0 ? 'Add at least one recipient' : 'Send now')}>
              {running ? 'Sending…' : 'Run Now'}
            </button>
            <button style={{ ...s.btnSecondary, color: '#dc2626', marginLeft: 'auto' }} onClick={remove}>
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  title: { fontSize: 18, fontWeight: 600, color: '#111827', marginBottom: 4 },
  desc: { fontSize: 13, color: '#6b7280', marginBottom: 20 },
  success: { background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: '#166534' },
  errorBox: { background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: '#991b1b' },
  card: { border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 12, background: '#fff' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', cursor: 'pointer' },
  cardTitle: { fontSize: 14, fontWeight: 600, color: '#111827', marginLeft: 12 },
  cardMeta: { fontSize: 12, color: '#6b7280', marginLeft: 12 },
  cardBody: { padding: '8px 16px 16px', borderTop: '1px solid #f3f4f6' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 16 },
  row: { marginBottom: 16 },
  field: { marginBottom: 16 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 },
  checkLabel: { fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 6 },
  input: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, outline: 'none', boxSizing: 'border-box' },
  btnPrimary: { padding: '8px 20px', fontSize: 13, background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500 },
  btnSecondary: { padding: '8px 14px', fontSize: 13, background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' },
  emailChip: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#f3f4f6', borderRadius: 16, padding: '4px 12px', fontSize: 12, marginRight: 6, marginBottom: 4 },
  removeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, padding: 0, lineHeight: 1 },
};
